import { Injectable, InjectProperty } from '@jack-henry/frontend-utils/di';
import { ObservationSource } from '@jack-henry/frontend-utils/observable';
import {
    ArpClient,
    CheckExceptionClient,
    CompanyAccountModelDto,
    DownloadClient,
    OutPutTypeDto,
} from '@treasury/api/channel';
import { mapAchExceptionFiltersToRequest } from '../arp/mappings/ach-exception-filters-mapping';
import { mapSearchParams } from '../arp/mappings/check-exception-search.mappers';
import { FiDate } from '../dates';
import { downloadBlob } from '../utilities/file-handling';
export interface DownloadConfig<T> {
    filterModel: T;
    isRecurring: boolean;
    disabled: boolean;
}
@Injectable()
export class DownloadService<T> {
    public configSource = new ObservationSource<DownloadConfig<T>>();

    @InjectProperty()
    declare private arpClient: ArpClient;

    @InjectProperty()
    declare private downloadClient: DownloadClient;

    @InjectProperty()
    declare private checkExceptionClient: CheckExceptionClient;

    public updateConfig(config: DownloadConfig<T>) {
        this.configSource.emit(config);
    }

    public async download(outputType: string, filterModel: object, pageId: string) {
        const isPdf = outputType.includes('PDF');
        const splitOutput = outputType.split(' ');
        const fileName =
            splitOutput.length > 1
                ? `${splitOutput[0]}-${new FiDate(new Date()).toIsoDate()}`
                : new FiDate(new Date()).toIsoDate();
        const fileType = isPdf ? 'pdf' : 'csv';
        let response;
        if (pageId === 'CheckExceptions') {
            const arpExceptionAccounts = (
                await this.arpClient.arpGetArpAccounts('WorkArpExceptions', {})
            ).data.map((account: CompanyAccountModelDto) => account.accountUniqueId);
            const parameters = { account: arpExceptionAccounts };
            const body = mapSearchParams(parameters);
            response = isPdf
                ? await this.checkExceptionClient.checkExceptionDownloadPdfCheckExceptions(body)
                : await this.checkExceptionClient.checkExceptionDownloadCsvCheckExceptions(body);
        } else {
            let parsedType = '';
            if (isPdf) {
                parsedType = 'PDF';
            } else {
                parsedType = outputType.includes('Detail') ? 'CsvInDetail' : 'CSV';
            }
            const typeKey = Object.keys(OutPutTypeDto).find(
                key => key.toLowerCase() === parsedType.toLowerCase()
            );
            const outputTypeId = OutPutTypeDto[typeKey as keyof typeof OutPutTypeDto];
            if (pageId === 'AchExceptionsList') {
                filterModel = mapAchExceptionFiltersToRequest(filterModel);
            }
            const model = {
                pageId: pageId,
                outputType: outputTypeId,
                includeHeader: true,
                confirmationId: 0,
                filterModel: { ...filterModel, page: pageId },
                downloadPdfTypeSelected: isPdf ? outputType : undefined,
                isPortrait: false,
            };
            response = await this.downloadClient.downloadPost(model);
        }
        const arrayBuffer = await response.arrayBuffer();
        const type = isPdf ? 'application/pdf' : 'application/octet-stream';
        const blob = new Blob([arrayBuffer], { type });
        return downloadBlob(blob, pageId, fileName, fileType);
    }
}
